import { waitingOn, WAITING_ON_US } from './conversationTriage';

// ─── The Conversations list: activity, filtering, and saying what is hidden ────
//
// WHY THIS EXISTS
// The 2026-09-08 audit went looking for the filter that was hiding web-chat
// threads and did not find one. `db.getAgentConversations()` is
// `select('*').order('started_at', desc)` with no `.eq()`, no date window and no
// channel clause, and `ConversationsView` mapped straight over what it was given.
// Nothing was being filtered out, so nothing could be un-filtered.
//
// That is a good answer, and it is also why this module exists: the view could
// not TELL you it wasn't filtering. It printed one number, "N total", with no
// way to distinguish "N is everything" from "N is what survived something". A
// recruiter looking for a chat thread had to take the list's word for it.
//
// So the filters here are real (channel and status), they default to showing
// everything, the header always reports "shown of total", and the empty state
// names the filter responsible instead of saying "No conversations yet" — which
// was, before this, the single most misleading string in the CRM: it asserted
// a fact about the database while describing the state of an array.
//
// PURE ON PURPOSE, same reason as ./messageStatus and ./leadClassification: this
// repo has jest via react-scripts but no @testing-library, so a pure function is
// the only thing that can actually be pinned by a test.

/** The sentinel every filter uses for "do not filter on this". */
export const ALL = 'all';

/** The filter state that shows every row. Frozen: it is compared by value. */
// `waiting` added 2026-09-18 (ESCALATION-LOOP-SPEC E). Three values: ALL, 'us',
// 'them' -- the same strings conversationTriage exports as WAITING_ON_US and
// WAITING_ON_THEM, so the filter and the pill cannot disagree about what they mean.
export const NO_FILTERS = Object.freeze({ channel: ALL, status: ALL, hideClosed: false, waiting: ALL });

/**
 * What the list opens on: everything except closed threads.
 *
 * Closed threads are retired, not deleted — recruiter-agent's sweeper closes a
 * thread after CONVERSATION_AUTOCLOSE_DAYS with no inbound, and the next inbound
 * reopens it. Left visible they would accumulate forever at the bottom of a list
 * whose whole job is "what needs attention", and the count in the header would
 * drift away from anything a recruiter cares about.
 *
 * Hidden, NOT filtered away silently. This module exists because the view could
 * not say what it was hiding (see the header comment), so `hideClosed` is a named
 * facet that describeFilters() reports and isUnfiltered() counts. Turning it off
 * shows every closed thread; nothing is unreachable.
 */
export const DEFAULT_FILTERS = Object.freeze({ channel: ALL, status: ALL, hideClosed: true, waiting: ALL });

/** True when no filter is narrowing the list — the "All conversations" state. */
export function isUnfiltered(filters) {
  const f = filters || {};
  return (f.channel || ALL) === ALL
    && (f.status || ALL) === ALL
    && (f.waiting || ALL) === ALL
    && f.hideClosed !== true;
}

// ─── Status ───────────────────────────────────────────────────────────────────
//
// The three states a thread can be in, from
// recruiter-agent/db/2026-09-09-conversation-lifecycle.sql. Kept as data rather
// than as a switch in the component so the copy is testable and so an unknown
// status renders as itself instead of disappearing.
export const CLOSED = 'closed';

const STATUS_PILLS = {
  active: {
    label: 'Active',
    tone: 'green',
    title: 'The agent may reply, subject to consent and quiet hours.',
  },
  handed_off: {
    label: 'You have it',
    tone: 'indigo',
    title: 'A recruiter replied through the CRM. The agent is muted on this thread.',
  },
  closed: {
    label: 'Closed',
    tone: 'grey',
    title: 'No inbound for the auto-close window. Reopens by itself if they text again.',
  },
};

/**
 * The pill for a thread's status. Never returns null: an unrecognised status
 * renders with its own raw text, because a status the CRM does not know about is
 * exactly the thing somebody needs to SEE rather than have hidden behind a blank.
 */
export function statusPill(conversation) {
  const raw = (conversation && conversation.status ? String(conversation.status) : '').trim();
  if (!raw) return { label: 'Unknown', tone: 'grey', title: 'This thread has no status.' };
  const known = STATUS_PILLS[raw.toLowerCase()];
  if (known) return { ...known };
  return {
    label: raw,
    tone: 'amber',
    title: 'The CRM does not recognise this status. It is shown as written.',
  };
}

/** True when this row is a closed thread. Normalised like every other read here. */
export function isClosed(conversation) {
  return (conversation && conversation.status
    ? String(conversation.status).trim().toLowerCase() : '') === CLOSED;
}

// ─── Activity ─────────────────────────────────────────────────────────────────
//
// LAST ACTIVITY DOES NOT COME FROM agent_conversations.last_message_at.
//
// That column is declared `not null default now()` and is READ as the ORDER BY in
// three of the worker's queries, but it is WRITTEN by nothing: there is no PATCH
// anywhere in recruiter-agent/src, and no trigger in the base schema or in either
// migration. Every row's last_message_at is therefore frozen at the moment the
// thread was created, and is a synonym for started_at.
//
// Trusting it would have produced a "Last activity" column that silently equalled
// the "Started" column beside it, which is worse than not having one — a thread
// alive for two days would read as untouched since the day it opened. So the real
// value is derived here, from max(agent_messages.created_at) per conversation.
// (Full finding: sites/tools/reports/RECRUITER-AGENT-CHAT-AUDIT-2026-09-08.md §4.)

/**
 * Fold the lightweight agent_messages refs into per-conversation
 * { count, lastAt } — the message count the list already showed, and the last
 * activity timestamp it could not show.
 *
 * `lastAt` is the maximum created_at as an ISO string, or null when a
 * conversation has no messages or no row carried a timestamp. Rows with an
 * unparseable created_at are counted but do not contribute a timestamp: an
 * unreadable date must not silently become "no activity", and must not become
 * NaN and poison a comparison either.
 */
export function activityByConversation(refs) {
  const out = new Map();
  (refs || []).forEach((r) => {
    if (!r || !r.conversation_id) return;
    const id = r.conversation_id;
    const prev = out.get(id) || { count: 0, lastAt: null };
    prev.count += 1;
    const t = r.created_at ? Date.parse(r.created_at) : NaN;
    if (!Number.isNaN(t)) {
      const prevT = prev.lastAt ? Date.parse(prev.lastAt) : -Infinity;
      if (t > prevT) prev.lastAt = r.created_at;
    }
    out.set(id, prev);
  });
  return out;
}

/** { count, lastAt } for one conversation; the empty shape when it has none. */
export function activityFor(activity, conversationId) {
  return (activity && activity.get(conversationId)) || { count: 0, lastAt: null };
}

/**
 * Most-recently-active first, falling back to started_at for a thread with no
 * messages — so a conversation row that exists but never got a turn logged still
 * sorts by when it appeared rather than sinking to the bottom forever.
 *
 * Ordering is NOT a filter: it never removes a row, and the "All conversations"
 * reset does not touch it. Returns a new array; the input is not mutated.
 */
export function sortByActivity(conversations, activity) {
  const key = (cv) => {
    const a = activityFor(activity, cv && cv.id);
    const raw = a.lastAt || (cv && cv.started_at);
    const t = raw ? Date.parse(raw) : NaN;
    return Number.isNaN(t) ? -Infinity : t;
  };
  return [...(conversations || [])].sort((x, y) => key(y) - key(x));
}

// ─── Filtering ────────────────────────────────────────────────────────────────

/**
 * The channel value a row should be filtered and badged by.
 *
 * Anything that is not a recognised channel is normalised to 'other' rather than
 * being dropped or being quietly drawn as SMS. The schema's CHECK allows
 * 'sms' | 'chat' | 'voice', but a NULL passes that CHECK, and a row whose channel
 * nobody anticipated is exactly the row somebody needs to see.
 */
export function channelOf(conversation) {
  const raw = (conversation && conversation.channel ? String(conversation.channel) : '').trim().toLowerCase();
  if (raw === 'sms' || raw === 'chat' || raw === 'voice') return raw;
  return 'other';
}

/** Every channel present in the data, in a stable order, for the filter control. */
export function channelsPresent(conversations) {
  const order = ['sms', 'chat', 'voice', 'other'];
  const seen = new Set((conversations || []).map(channelOf));
  return order.filter((c) => seen.has(c));
}

/** Every status present in the data, sorted, for the filter control. */
export function statusesPresent(conversations) {
  const seen = new Set(
    (conversations || [])
      .map((cv) => (cv && cv.status ? String(cv.status).trim() : ''))
      .filter(Boolean)
  );
  return [...seen].sort();
}

/**
 * Apply the filters. `ALL` on a facet means that facet does not narrow anything,
 * so NO_FILTERS returns the input rows unchanged — the property the
 * "All conversations" button depends on.
 */
/**
 * `messages` is optional and is only needed for the `waiting` facet. Passing it is
 * how the caller opts into that filter; omitting it leaves every thread matching,
 * so no existing caller changes behaviour by not passing it.
 */
export function applyFilters(conversations, filters, messages) {
  const f = filters || {};
  const channel = f.channel || ALL;
  const status = f.status || ALL;
  const waiting = f.waiting || ALL;
  return (conversations || []).filter((cv) => {
    if (channel !== ALL && channelOf(cv) !== channel) return false;
    if (status !== ALL && (cv && cv.status ? String(cv.status).trim() : '') !== status) return false;
    // hideClosed is subordinate to an explicit status filter: asking for
    // status=closed and getting nothing back would be absurd, so an explicit
    // choice always wins over the default.
    if (f.hideClosed === true && status === ALL && isClosed(cv)) return false;
    // The waiting facet needs the message refs. Without them it cannot answer, and
    // it does NOT guess: a filter that silently hid rows because its input was
    // missing is the exact failure this module exists to prevent.
    if (waiting !== ALL) {
      if (!messages) return true;
      if (waitingOn(messages, cv && cv.id) !== waiting) return false;
    }
    return true;
  });
}

// ─── Saying what is hidden ────────────────────────────────────────────────────

const CHANNEL_LABELS = { sms: 'SMS', chat: 'web chat', voice: 'voice', other: 'an unrecognised channel' };

/**
 * A human sentence naming the ACTIVE filters, or null when nothing is filtered.
 *
 * Used for the empty state. "No conversations yet" was a claim about the
 * database made by code that could only see an array, and it was wrong in the
 * one case that mattered — rows present, filter hiding them. When nothing is
 * filtered this returns null and the caller says something honest about the
 * table being genuinely empty instead.
 */
export function describeFilters(filters) {
  const f = filters || {};
  const parts = [];
  if ((f.channel || ALL) !== ALL) parts.push(`channel is ${CHANNEL_LABELS[f.channel] || f.channel}`);
  if ((f.status || ALL) !== ALL) parts.push(`status is ${f.status}`);
  if (f.hideClosed === true && (f.status || ALL) === ALL) parts.push('closed threads are hidden');
  if ((f.waiting || ALL) !== ALL) {
    parts.push(f.waiting === WAITING_ON_US ? 'only threads waiting on us' : 'only threads waiting on the candidate');
  }
  if (parts.length === 0) return null;
  return parts.join(' and ');
}

/**
 * The header line: what is on screen, out of what exists.
 *
 * ALWAYS states the total, even when nothing is filtered, because the whole
 * point is that "you are seeing everything" should be readable rather than
 * assumed. `total` is the number of rows LOADED, which is not necessarily the
 * number of rows in the table — see the truncation note in the audit report.
 */
export function countLabel(shown, total) {
  const s = Number(shown) || 0;
  const t = Number(total) || 0;
  if (s === t) return `${t} total`;
  return `${s} of ${t} shown`;
}
